/**
 * Java Analysis Executor
 *
 * Handles the 'java-analysis' and 'pipeline-java-analysis' node execution.
 * Calls the ModernizeIT API Layer /javaanalysis endpoint.
 *
 * API Flow (WORKER MODE):
 * 1. Verify upstream service generation job is 'completed' (poll if needed)
 * 2. POST /javaanalysis -> returns job_id with status QUEUED
 * 3. Poll GET /jobs/{job_id} -> wait until status = 'completed'
 * 4. Extract results from job.output_data
 *
 * CRITICAL: Must wait for upstream service generation to complete before starting.
 * The generated Java files from service generation are the input for analysis.
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the java analysis node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing Java analysis...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Verify upstream service generation job exists
  const svcgenJobId = context.jobs?.service_generation_job_id
  if (!svcgenJobId) {
    throw new Error('Java Analysis requires service generation job ID from Service Generation node. Run Service Generation first.')
  }

  // 2. Verify upstream service generation job is completed (poll if needed)
  console.log(`[JavaAnalysisExecutor] Verifying upstream service generation job ${svcgenJobId} is completed...`)
  onProgress(2, 'Waiting for Service Generation to complete...')

  try {
    // Poll the service generation job until it's completed
    await pollJobUntilComplete(svcgenJobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 1000,
      onProgress: (progress, message) => {
        // Map upstream polling progress from 2-8%
        const mappedProgress = 2 + (progress * 0.06)
        onProgress(mappedProgress, `Waiting for Service Generation: ${message}`)
      },
      signal
    })
    console.log(`[JavaAnalysisExecutor] Upstream service generation job ${svcgenJobId} is completed`)
  } catch (error) {
    throw new Error(`Upstream Service Generation job failed: ${error.message}`)
  }

  onProgress(8, 'Service Generation complete, starting Java analysis...')

  // 3. Build the request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id,
    svcgen_job_id: svcgenJobId,
    language: config.language || 'java',
    workflow_run_id: context.workflow_run_id || null,
  }

  onProgress(10, 'Submitting Java analysis job...')

  // 4. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/javaanalysis`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    // Check for API-level error in response
    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Java analysis failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  console.log(`[JavaAnalysisExecutor] Job created: ${jobId}, polling until completed...`)
  onProgress(15, `Job started: ${jobId}`)

  // 5. Poll for completion using generic jobs endpoint
  const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
    maxWaitSeconds: 600,
    pollInterval: 1000,
    onProgress: (progress, message) => {
      // Map polling progress from 15-95%
      const mappedProgress = 15 + (progress * 0.80)
      onProgress(mappedProgress, message)
    },
    signal
  })

  console.log('[JavaAnalysisExecutor] Job completed, extracting results from output_data')
  onProgress(95, 'Extracting results...')

  // 6. Extract results from job.output_data (worker mode)
  const outputData = completedJob.output_data
  if (!outputData) {
    throw new Error('Job completed but has no output_data')
  }

  onProgress(100, 'Java analysis complete')

  // 7. Return formatted result
  return formatResult(jobId, outputData, context.jobs)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data, contextJobs = {}) {
  console.log('[JavaAnalysisExecutor] formatResult called with contextJobs:', contextJobs)

  const resultJobs = {
    ...contextJobs,  // Preserve job chain from upstream nodes
    java_analysis_job_id: jobId,
  }

  console.log('[JavaAnalysisExecutor] Returning jobs:', resultJobs)

  return {
    // Job tracking — preserve upstream job IDs for downstream nodes
    jobs: resultJobs,

    // Status
    success: data.success !== false,
    status: data.status || 'completed',

    // Summary statistics
    summary: data.summary || {},

    // Graph loading results
    graph: data.graph || null,

    // Execution metrics
    duration_ms: data.duration_ms,

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
